import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpDown, Tag, Calendar, ChevronDown, X } from 'lucide-react';
import useTicketingStore, {
  TicketSearchByEnum,
  TicketSortByEnum,
  TicketSortKeyEnum,
  TicketSortOrderEnum,
} from '@/stores/useTicketingStore';
import { TicketFilters } from '@/stores';
import { getStatusTranslationKey, NormalizedTicketStatus } from '@/utils/ticketStatus';
import { useQueryClient } from '@tanstack/react-query';
import TicketSearchPill from './TicketSearchPill';
import TicketFilterModal from '../FiltersModal/TicketFilterModal';

/* ─── Design tokens (matching OrdersFilterRow) ─── */
const C = {
  border: 'rgb(232,230,227)',
  mutedFg: 'rgb(115,123,140)',
  fg: 'rgb(57,57,60)',
  white: '#ffffff',
  primaryBg: 'rgba(237,224,74,0.1)',
  primaryBorder: 'rgb(237,224,74)',
} as const;

/* ─── Status options ─── */
const TICKET_STATUSES = ['new', 'open', 'pending', 'on-hold', 'solved', 'closed'] as const;

/* ─── Sort options ─── */
export const ticketSortOptions = {
  [TicketSortKeyEnum.CREATED_DESC]: { label: 'sort_newest_created', sort_by: TicketSortByEnum.CREATED_AT, sort_order: TicketSortOrderEnum.DESC },
  [TicketSortKeyEnum.CREATED_ASC]: { label: 'sort_oldest_created', sort_by: TicketSortByEnum.CREATED_AT, sort_order: TicketSortOrderEnum.ASC },
  [TicketSortKeyEnum.UPDATED_DESC]: { label: 'sort_recently_updated', sort_by: TicketSortByEnum.UPDATED_AT, sort_order: TicketSortOrderEnum.DESC },
  [TicketSortKeyEnum.UPDATED_ASC]: { label: 'sort_least_recently_updated', sort_by: TicketSortByEnum.UPDATED_AT, sort_order: TicketSortOrderEnum.ASC },
};

const SORT_OPTIONS = [
  { key: TicketSortKeyEnum.CREATED_DESC, labelKey: 'sort_newest_created', sort_by: TicketSortByEnum.CREATED_AT, sort_order: TicketSortOrderEnum.DESC },
  { key: TicketSortKeyEnum.CREATED_ASC, labelKey: 'sort_oldest_created', sort_by: TicketSortByEnum.CREATED_AT, sort_order: TicketSortOrderEnum.ASC },
  { key: TicketSortKeyEnum.UPDATED_DESC, labelKey: 'sort_recently_updated', sort_by: TicketSortByEnum.UPDATED_AT, sort_order: TicketSortOrderEnum.DESC },
  { key: TicketSortKeyEnum.UPDATED_ASC, labelKey: 'sort_least_recently_updated', sort_by: TicketSortByEnum.UPDATED_AT, sort_order: TicketSortOrderEnum.ASC },
] as const;

/* ─── Reusable FilterPill dropdown ─── */
interface FilterPillProps {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  children: React.ReactNode;
}

function FilterPill({ icon, label, active, children }: FilterPillProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className='relative' ref={ref}>
      <button
        type='button'
        onClick={() => setOpen(!open)}
        className='inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-solid text-[12px] font-semibold transition-colors shrink-0 cursor-pointer'
        style={{
          borderColor: active ? C.primaryBorder : C.border,
          backgroundColor: active ? C.primaryBg : C.white,
          color: active ? C.fg : C.mutedFg,
        }}
      >
        {icon}
        {label}
        <ChevronDown className='w-3.5 h-3.5' />
      </button>
      {open && (
        <div
          className='absolute top-full start-0 mt-1 z-50 bg-white rounded-xl border border-solid py-1.5 min-w-[180px]'
          style={{ borderColor: C.border, boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.10)' }}
        >
          {React.Children.map(children, (child) =>
            React.isValidElement(child) ? React.cloneElement(child as React.ReactElement<any>, { onClose: () => setOpen(false) }) : child
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Main Component ─── */
interface Props {
  handleSearch: () => void;
  ticketFilters?: TicketFilters;
  handleResetFilters: () => void;
}

const TicketFilterHeader = ({ handleSearch, ticketFilters, handleResetFilters }: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const {
    resetFilers,
    sortKey,
    setSortKey,
    searchText,
    searchBy,
    setSearchData,
  } = useTicketingStore();

  const [filtersModalOpen, setFiltersModalOpen] = useState(false);

  const hasStatusFilter = (ticketFilters?.statusFilter?.length ?? 0) > 0;
  const hasDateFilter = !!ticketFilters?.createdAtFilter;
  const hasAnyFilter = hasStatusFilter || hasDateFilter;

  const handleReset = () => {
    resetFilers();
    handleResetFilters();
    setTimeout(() => queryClient.invalidateQueries(['tickets']), 300);
  };

  useEffect(() => {
    return () => { handleReset(); };
  }, []);

  return (
    <div className='flex items-center gap-2 flex-wrap w-full'>
      {/* Ticket search pill — left side */}
      <TicketSearchPill
        searchText={searchText}
        searchBy={searchBy}
        onChange={(text, field) => setSearchData({ searchText: text, searchBy: field })}
        onClear={() => setSearchData({ searchText: undefined, searchBy: undefined })}
      />

      {/* Spacer */}
      <div className='flex-1' />

      {/* Status/Date filter pill — opens modal */}
      <button
        type='button'
        onClick={() => setFiltersModalOpen(true)}
        className='inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-solid text-[12px] font-semibold transition-colors shrink-0 cursor-pointer'
        style={{
          borderColor: hasAnyFilter ? C.primaryBorder : C.border,
          backgroundColor: hasAnyFilter ? C.primaryBg : C.white,
          color: hasAnyFilter ? C.fg : C.mutedFg,
        }}
      >
        <Tag className='w-3.5 h-3.5' />
        {hasAnyFilter
          ? `${String(t('filters'))} (${(ticketFilters?.statusFilter?.length ?? 0) + (hasDateFilter ? 1 : 0)})`
          : String(t('filters'))}
        <ChevronDown className='w-3.5 h-3.5' />
      </button>

      {/* Sort pill */}
      <FilterPill
        icon={<ArrowUpDown className='w-3.5 h-3.5' />}
        label={String(t(SORT_OPTIONS.find((o) => o.key === sortKey)?.labelKey ?? 'sort'))}
        active={sortKey !== TicketSortKeyEnum.CREATED_DESC}
      >
        <SortContent
          t={t}
          currentKey={sortKey}
          onSelect={(key) => setSortKey(key)}
        />
      </FilterPill>

      {/* Clear all */}
      {hasAnyFilter && (
        <button
          type='button'
          onClick={handleReset}
          className='text-[12px] text-[rgb(239,68,68)] font-medium cursor-pointer bg-transparent border-none p-0 hover:underline'
        >
          {String(t('clear_all'))}
        </button>
      )}

      {/* Filter modal */}
      <TicketFilterModal
        visible={filtersModalOpen}
        onClose={() => setFiltersModalOpen(false)}
        onSubmit={() => { handleSearch(); setFiltersModalOpen(false); }}
      />
    </div>
  );
};


/* ─── Sort dropdown content ─── */
function SortContent({ t, currentKey, onSelect, onClose }: {
  t: (key: string) => string;
  currentKey: TicketSortKeyEnum;
  onSelect: (key: TicketSortKeyEnum) => void;
  onClose?: () => void;
}) {
  return (
    <>
      {SORT_OPTIONS.map((opt) => (
        <button
          key={opt.key}
          type='button'
          onClick={() => { onSelect(opt.key); onClose?.(); }}
          className={`w-full text-start px-3.5 py-2 text-[12px] cursor-pointer hover:bg-gray-50 transition-colors ${currentKey === opt.key ? 'font-bold text-[rgb(57,57,60)]' : 'text-[rgb(115,123,140)]'}`}
        >
          {String(t(opt.labelKey))}
        </button>
      ))}
    </>
  );
}

export default TicketFilterHeader;
